import dayjs from '@/config/dayjs';
import { getCurrencyStringTitleForChart } from '../currency';

export const TICK_DATE_FORMAT = 'MMM YY';
const TOOLTIP_DATE_FORMAT = 'MMMM YYYY';

const toDate = (value: Date | string | number): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

export const formatChartDateAxis = (
  value: Date | string | number | null | undefined,
  ctx?: { location?: string },
): string => {
  if (value === null || value === undefined) return '';
  const d = toDate(value);
  if (!d) return '';
  const pattern =
    ctx?.location === 'tooltip' ? TOOLTIP_DATE_FORMAT : TICK_DATE_FORMAT;
  return dayjs(d).format(pattern);
};

export const formatPercentAxis = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return `${Math.round(value).toString()}%`;
};

/**
 * Whole-number axis for charts that count things rather than measure money or
 * share. Rounding keeps a small count axis from ticking in fractions of a
 * client, which no chart of people should ever show.
 */
export const formatCountAxis = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return Math.round(value).toLocaleString();
};

export const responsiveTickInterval = (
  viewportWidth: number,
): ((value: unknown, index: number) => boolean) => {
  if (viewportWidth < 400) return (_v, i) => i % 2 === 0;
  return () => true;
};

/**
 * Compute a tick filter predicate for categorical x-axis. Uses the total
 * count of categories and the viewport width to estimate how many labels
 * fit without overlap. Returns a `(value, index) => boolean` predicate where
 * the step is `Math.ceil(MIN_SPACE / spacePerCategory)` (e.g. step=2 means
 * "show every 2nd label").
 */
export const adaptiveCategoricalTickInterval = (
  totalCategories: number,
  viewportWidth: number,
  minSpacePerLabelPx: number = 70,
): ((value: unknown, index: number) => boolean) => {
  if (totalCategories <= 0) return () => true;
  const availableWidth = Math.max(viewportWidth * 0.75, 200);
  const spacePerCategory = availableWidth / totalCategories;
  if (spacePerCategory >= minSpacePerLabelPx) return () => true;
  const step = Math.max(2, Math.ceil(minSpacePerLabelPx / spacePerCategory));
  return (_v, i) => i % step === 0;
};

export const computePercentAxisMax = (
  values: readonly (number | null | undefined)[],
): number | undefined => {
  if (!values.length) return undefined;
  let max = 0;
  for (const v of values) {
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    const abs = Math.abs(v);
    if (abs > max) max = abs;
  }
  if (max <= 0) return 10;
  return Math.min(Math.ceil((max + 1) / 10) * 10, 100);
};

export { getCurrencyStringTitleForChart };
